import * as React from "react";
import { FC } from "react";
import { useDragLayer, XYCoord } from "react-dnd";
import { ChessPiece } from "../models/chessPiece";
import { Grid } from "../models/grid";
import { pieceIcon } from "./pieceIcons";

export type PieceDragLayerProps = {
  state: Grid;
  piece?: ChessPiece;
};

const layerStyles: React.CSSProperties = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 1300,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

const transform = (offset: XYCoord | null): React.CSSProperties => {
  if (!offset) return { display: "none" };
  return { transform: `translate(${offset.x}px, ${offset.y}px)` };
};

/**
 * A custom drag preview rendered by react-dnd. It follows the pointer with the
 * lifted letter and a badge of the chess piece whose move is being performed —
 * making it unmistakable that you're moving the tile *as that piece*.
 * Works for both the HTML5 and touch backends.
 */
export const PieceDragLayer: FC<PieceDragLayerProps> = ({ state, piece }) => {
  const { isDragging, item, offset } = useDragLayer((monitor) => ({
    item: monitor.getItem() as [number, number] | null,
    offset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  // `item` is only our tile tuple for real piece drags. Native drags (e.g.
  // dragging one of the piece-indicator <img>s below the board) report
  // isDragging with a non-array item, so guard before destructuring.
  if (!isDragging || !Array.isArray(item)) return null;

  const [x, y] = item;
  const letter = state[y]?.[x];
  const icon = pieceIcon(piece, true);

  return (
    <div style={layerStyles}>
      <div style={transform(offset)}>
        <div className="dragTile">
          <span className="dragTile-letter">{letter}</span>
          {icon && (
            <span className="dragTile-badge">
              <img src={icon} alt={piece} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
